import {
  collection,
  addDoc,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  QueryDocumentSnapshot,
  DocumentData,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getFirebaseDB, getFirebaseStorage } from "@/lib/firebase";
import { Property, Unit, PropertyStructure } from "@/types/types";
import moment from "moment";

// Types for the service
export interface PaginatedResult<T> {
  data: T[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
  total?: number;
}
export interface CreatePropertyData {
  title: string;
  address: string;
  type: Property["type"];
  structure: PropertyStructure; // "single-unit" or "multi-unit"
  bedrooms: number;
  bathrooms: number;
  area: number;
  rent: number;
  status: Property["status"];

  // Multi-unit specific
  units?: Partial<Unit>[]; // Array of units for multi-unit properties
  totalFloors?: number;
  totalUnits?: number;
  buildingFacilities?: string[];

  // Approval fields
  approvalStatus?: Property["approvalStatus"];

  // Agent/Landlord fields
  ownerId: string; // Property owner
  listerId: string; // Person listing (agent or owner)
  isAgentListing?: boolean;

  // Optional fields
  maintenanceIssues?: number;
  tenantId?: string;
  tenant?: string;
  leaseEnd?: string;
  image?: string;
  imageUrls?: string[];
  description?: string;
  amenities?: string[];
  leaseDuration?: number;
  securityDeposit?: number;
  paymentTerms?: string;
}

export interface UpdatePropertyData {
  title?: string;
  address?: string;
  type?: Property["type"];
  structure?: PropertyStructure;
  bedrooms?: number;
  bathrooms?: number;
  area?: number;
  rent?: number;
  status?: Property["status"];
  approvalStatus?: Property["approvalStatus"];
  maintenanceIssues?: number;
  tenantId?: string;
  tenant?: string;
  leaseEnd?: string;
  image?: string;
  imageUrls?: string[];
  description?: string;
  amenities?: string[];
  averageRating?: number;
  reviewCount?: number;
  viewCount?: number;
  totalFloors?: number;
  totalUnits?: number;
  buildingFacilities?: string[];
  units?: Partial<Unit>[];
}

function generateFirestoreData(
  propertyData: CreatePropertyData,
  userId: string
) {
  const now = moment().toISOString();

  return {
    ...propertyData,
    userId,
    createdAt: now,
    lastUpdated: now,
    // Default values
    maintenanceIssues: propertyData.maintenanceIssues || 0,
    amenities: propertyData.amenities || [],
    // Default approval status is pending (requires admin approval)
    approvalStatus: propertyData.approvalStatus || "pending",
    // Owner and lister default to same person unless agent listing
    ownerId: propertyData.ownerId || userId,
    listerId: propertyData.listerId || userId,
    isAgentListing: propertyData.isAgentListing || false,
    // Rating defaults
    averageRating: 0,
    reviewCount: 0,
  };
}

/**
 * Add a new property to Firestore
 * @param propertyData - The property data to create
 */
export async function addProperty(
  propertyData: CreatePropertyData
): Promise<Property> {
  try {
    // Use ownerId from propertyData, which should be set by the calling code
    const userId = propertyData.ownerId || propertyData.listerId;

    if (!userId) {
      throw new Error("User ID is required to create a property");
    }

    const firestoreData = generateFirestoreData(propertyData, userId);

    const db = getFirebaseDB();
    if (!db) throw new Error("Firebase DB not available");

    console.log(
      "Creating property with userId:",
      userId,
      "data:",
      firestoreData
    );

    const docRef = await addDoc(collection(db, "properties"), firestoreData);

    console.log("Property created with ID:", docRef.id);

    return {
      id: docRef.id,
      ...firestoreData,
    } as Property;
  } catch (error) {
    console.error("Error adding property:", error);
    throw new Error(
      `Failed to add property: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
}

/**
 * Get all properties for a specific user (with pagination)
 * @param userId - ID of the user to get properties for
 * @param pageSize - Number of items per page (default: 20)
 * @param lastDocument - Last document from previous page (for pagination)
 */
export async function getProperties(
  userId: string,
  pageSize: number = 20,
  lastDocument?: QueryDocumentSnapshot<DocumentData>
): Promise<PaginatedResult<Property>> {
  try {
    const db = getFirebaseDB();
    if (!db) throw new Error("Firebase DB not available");

    console.log("Querying properties for userId:", userId);

    let q = query(
      collection(db, "properties"),
      where("userId", "==", userId),
      orderBy("createdAt", "desc"),
      limit(pageSize + 1) // Fetch one extra to check if there are more
    );

    if (lastDocument) {
      q = query(q, startAfter(lastDocument));
    }

    const querySnapshot = await getDocs(q);
    const properties: Property[] = [];

    console.log("Query returned", querySnapshot.size, "documents");

    const hasMore = querySnapshot.size > pageSize;
    const docs = hasMore
      ? querySnapshot.docs.slice(0, pageSize)
      : querySnapshot.docs;

    docs.forEach((doc) => {
      const data = doc.data();
      console.log(
        "Property doc:",
        doc.id,
        "userId:",
        data.userId,
        "ownerId:",
        data.ownerId
      );
      properties.push({
        id: doc.id,
        ...data,
      } as Property);
    });

    return {
      data: properties,
      lastDoc: docs.length > 0 ? docs[docs.length - 1] : null,
      hasMore,
    };
  } catch (error) {
    console.error("Error getting properties:", error);
    throw new Error(
      `Failed to get properties: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
}

/**
 * Get all properties for a specific user (backward compatible - no pagination)
 * @deprecated Use getProperties with pagination instead
 */
export async function getAllUserProperties(
  userId: string
): Promise<Property[]> {
  const result = await getProperties(userId, 1000); // Large limit for backward compat
  return result.data;
}

/**
 * Get all properties (admin only - no userId filter) with pagination
 * This function should only be called by users with admin or super-admin role
 */
export async function getAllProperties(
  pageSize: number = 50,
  lastDocument?: QueryDocumentSnapshot<DocumentData>
): Promise<PaginatedResult<Property>> {
  try {
    const db = getFirebaseDB();
    if (!db) throw new Error("Firebase DB not available");

    let q = query(
      collection(db, "properties"),
      orderBy("createdAt", "desc"),
      limit(pageSize + 1)
    );

    if (lastDocument) {
      q = query(q, startAfter(lastDocument));
    }

    const querySnapshot = await getDocs(q);
    const properties: Property[] = [];

    const hasMore = querySnapshot.size > pageSize;
    const docs = hasMore
      ? querySnapshot.docs.slice(0, pageSize)
      : querySnapshot.docs;

    docs.forEach((doc) => {
      properties.push({
        id: doc.id,
        ...doc.data(),
      } as Property);
    });

    return {
      data: properties,
      lastDoc: docs.length > 0 ? docs[docs.length - 1] : null,
      hasMore,
    };
  } catch (error) {
    console.error("Error getting all properties:", error);
    throw new Error(
      `Failed to get all properties: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
}

/**
 * Get a property by its ID
 * @param propertyId - ID of the property to retrieve
 */
export async function getPropertyById(
  propertyId: string
): Promise<Property | null> {
  try {
    const db = getFirebaseDB();
    if (!db) throw new Error("Firebase DB not available");

    const docRef = doc(db, "properties", propertyId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data(),
      } as Property;
    } else {
      return null;
    }
  } catch (error) {
    console.error("Error getting property:", error);
    throw new Error(
      `Failed to get property: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
}

/**
 * Update an existing property
 * @param propertyId - ID of the property to update
 * @param updateData - Data to update
 */
export async function updateProperty(
  propertyId: string,
  updateData: UpdatePropertyData
): Promise<Property | null> {
  try {
    const db = getFirebaseDB();
    if (!db) throw new Error("Firebase DB not available");

    const docRef = doc(db, "properties", propertyId);

    // First, get the current property to merge data
    const currentProp = await getPropertyById(propertyId);
    if (!currentProp) {
      throw new Error("Property not found");
    }

    const updatedData = {
      ...updateData,
      lastUpdated: moment().toISOString(),
    };

    await updateDoc(docRef, updatedData);

    // Return updated property
    return {
      ...currentProp,
      ...updatedData,
    } as Property;
  } catch (error) {
    console.error("Error updating property:", error);
    throw new Error(
      `Failed to update property: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
}

/**
 * Delete a property from Firestore
 * @param propertyId - ID of the property to delete
 */
export async function deleteProperty(propertyId: string): Promise<void> {
  try {
    const db = getFirebaseDB();
    if (!db) throw new Error("Firebase DB not available");

    const docRef = doc(db, "properties", propertyId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error("Error deleting property:", error);
    throw new Error(
      `Failed to delete property: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
}

/**
 * Update property status
 * @param propertyId - ID of the property
 * @param status - New status
 */
export async function updatePropertyStatus(
  propertyId: string,
  status: Property["status"]
): Promise<Property | null> {
  return updateProperty(propertyId, { status });
}

/**
 * Assign a tenant to a property
 * @param propertyId - ID of the property
 * @param tenantId - ID of the tenant
 * @param tenantName - Name of the tenant
 * @param leaseEnd - Lease end date
 */
export async function assignTenantToProperty(
  propertyId: string,
  tenantId: string,
  tenantName: string,
  leaseEnd?: string
): Promise<Property | null> {
  return updateProperty(propertyId, {
    tenantId,
    tenant: tenantName,
    leaseEnd,
    status: "occupied",
  });
}

/**
 * Remove tenant from property (make vacant)
 * @param propertyId - ID of the property
 */
export async function removeTenantFromProperty(
  propertyId: string
): Promise<Property | null> {
  return updateProperty(propertyId, {
    tenantId: undefined,
    tenant: undefined,
    leaseEnd: undefined,
    status: "vacant",
  });
}

/**
 * Search for properties with filters
 * @param filters - Filter options (location, minPrice, maxPrice, type, status)
 */
export interface PropertySearchFilters {
  location?: string;
  minPrice?: number;
  maxPrice?: number;
  type?: string;
  status?: Property["status"];
  limit?: number;
  includeUnapproved?: boolean; // For admin/landlord views
}

export async function searchProperties(
  filters: PropertySearchFilters = {},
  pageSize: number = 20,
  lastDocument?: QueryDocumentSnapshot<DocumentData>
): Promise<PaginatedResult<Property>> {
  try {
    const db = getFirebaseDB();
    if (!db) throw new Error("Firebase DB not available");

    // For public marketplace, filter by approved status at query level for efficiency
    let q = filters.includeUnapproved
      ? query(
          collection(db, "properties"),
          orderBy("createdAt", "desc"),
          limit(pageSize * 3) // Fetch more since we filter client-side
        )
      : query(
          collection(db, "properties"),
          where("approvalStatus", "==", "approved"),
          orderBy("createdAt", "desc"),
          limit(pageSize * 3)
        );

    if (lastDocument) {
      q = query(q, startAfter(lastDocument));
    }

    const querySnapshot = await getDocs(q);
    let properties: Property[] = [];

    querySnapshot.forEach((doc) => {
      properties.push({
        id: doc.id,
        ...doc.data(),
      } as Property);
    });

    // Apply filters in-app
    if (filters.location) {
      properties = properties.filter((p) =>
        p.address?.toLowerCase().includes(filters.location!.toLowerCase())
      );
    }

    if (filters.minPrice !== undefined) {
      properties = properties.filter((p) => p.rent >= filters.minPrice!);
    }

    if (filters.maxPrice !== undefined) {
      properties = properties.filter((p) => p.rent <= filters.maxPrice!);
    }

    if (filters.type) {
      properties = properties.filter((p) => p.type === filters.type);
    }

    if (filters.status) {
      properties = properties.filter((p) => p.status === filters.status);
    }

    // Apply pagination after filtering
    const paginatedProperties = properties.slice(0, pageSize);
    const hasMore = properties.length > pageSize;

    return {
      data: paginatedProperties,
      lastDoc: querySnapshot.docs[querySnapshot.docs.length - 1] || null,
      hasMore,
    };
  } catch (error) {
    console.error("Error searching properties:", error);
    throw new Error(
      `Failed to search properties: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
}

/**
 * Get featured properties (limit, highly rated, or recently added)
 * Only returns approved properties for public display
 * @param limit - Number of properties to return
 */
export async function getFeaturedProperties(
  limit: number = 6
): Promise<Property[]> {
  try {
    const db = getFirebaseDB();
    if (!db) {
      console.warn("Firebase DB not available");
      return [];
    }

    const q = query(collection(db, "properties"), orderBy("createdAt", "desc"));

    const querySnapshot = await getDocs(q);
    const properties: Property[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // Only include approved properties
      if (data.approvalStatus === "approved") {
        properties.push({
          id: doc.id,
          ...data,
        } as Property);
      }
    });

    return properties.slice(0, limit);
  } catch (error) {
    // Log the error but return empty array instead of throwing
    // This prevents the homepage from crashing for unauthenticated users
    console.warn(
      "Could not fetch featured properties:",
      error instanceof Error ? error.message : "Unknown error"
    );
    return [];
  }
}

/**
 * Upload property images to Firebase Storage
 * @param files - Array of image files to upload
 * @param propertyId - ID of the property (optional, used for folder structure)
 * @returns Array of download URLs for uploaded images
 */
export async function uploadPropertyImages(
  files: File[],
  propertyId: string = "new-property"
): Promise<string[]> {
  if (!files || files.length === 0) {
    return [];
  }

  try {
    const storage = getFirebaseStorage();
    if (!storage) throw new Error("Firebase Storage not available");

    const uploadedUrls: string[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // Validate file
      if (!file.type.startsWith("image/")) {
        throw new Error(
          `File ${i + 1}: Invalid file type. Only images are allowed.`
        );
      }

      if (file.size > 5 * 1024 * 1024) {
        // 5MB limit
        throw new Error(`File ${i + 1}: File size exceeds 5MB limit.`);
      }

      // Create a unique filename with timestamp
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(2, 9);
      const filename = `${timestamp}-${randomId}-${file.name}`;

      // Create storage reference
      const storageRef = ref(
        storage,
        `properties/${propertyId}/images/${filename}`
      );

      // Upload file
      const snapshot = await uploadBytes(storageRef, file);

      // Get download URL
      const downloadUrl = await getDownloadURL(snapshot.ref);
      uploadedUrls.push(downloadUrl);

      console.log(`Image ${i + 1} uploaded successfully:`, downloadUrl);
    }

    return uploadedUrls;
  } catch (error) {
    console.error("Error uploading property images:", error);
    throw new Error(
      `Failed to upload images: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
}

/**
 * Increment property view count
 * Uses debouncing via local storage to prevent spam
 */
export async function incrementPropertyViews(propertyId: string): Promise<void> {
  try {
    // Check if user has viewed this property recently (within 5 minutes)
    const viewKey = `property_view_${propertyId}`;
    const lastView = localStorage.getItem(viewKey);
    const now = Date.now();

    if (lastView && now - parseInt(lastView) < 5 * 60 * 1000) {
      // Viewed within last 5 minutes, don't increment
      return;
    }

    const db = getFirebaseDB();
    if (!db) throw new Error("Firebase not initialized");

    const propertyRef = doc(db, "properties", propertyId);
    const propertySnap = await getDoc(propertyRef);

    if (!propertySnap.exists()) {
      throw new Error("Property not found");
    }

    const currentViews = propertySnap.data()?.viewCount || 0;
    await updateDoc(propertyRef, {
      viewCount: currentViews + 1,
    });

    // Store timestamp of this view
    localStorage.setItem(viewKey, now.toString());
  } catch (error) {
    console.error("Error incrementing property views:", error);
    // Don't throw - view counting shouldn't break the page
  }
}

/**
 * Get similar properties based on type, location, and price
 */
export async function getSimilarProperties(
  propertyId: string,
  limitCount: number = 4
): Promise<Property[]> {
  try {
    const db = getFirebaseDB();
    if (!db) throw new Error("Firebase not initialized");

    // First, get the current property
    const currentPropertySnap = await getDoc(doc(db, "properties", propertyId));
    if (!currentPropertySnap.exists()) {
      return [];
    }

    const currentProperty = {
      ...currentPropertySnap.data(),
      id: currentPropertySnap.id,
    } as Property;

    // Build query for similar properties
    const propertiesRef = collection(db, "properties");
    
    // Find properties with same type and status
    const q = query(
      propertiesRef,
      where("type", "==", currentProperty.type),
      where("approvalStatus", "==", "approved"),
      where("status", "==", "vacant"),
      orderBy("createdAt", "desc"),
      limit(20) // Get more to filter
    );

    const snapshot = await getDocs(q);
    const properties: Property[] = [];

    snapshot.forEach((doc) => {
      if (doc.id === propertyId) return; // Skip current property

      const data = doc.data();
      const property: Property = {
        ...data,
        id: doc.id,
        createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
        lastUpdated: data.lastUpdated?.toDate?.()?.toISOString() || data.lastUpdated,
        approvedAt: data.approvedAt?.toDate?.()?.toISOString() || data.approvedAt,
        leaseEnd: data.leaseEnd?.toDate?.()?.toISOString() || data.leaseEnd,
      } as Property;

      // Calculate similarity score
      let score = 0;

      // Same number of bedrooms (+3 points)
      if (property.bedrooms === currentProperty.bedrooms) score += 3;
      // ±1 bedroom (+1 point)
      else if (Math.abs(property.bedrooms - currentProperty.bedrooms) === 1) score += 1;

      // Similar price (within 20%) (+2 points)
      const priceDiff = Math.abs(property.rent - currentProperty.rent) / currentProperty.rent;
      if (priceDiff <= 0.2) score += 2;
      else if (priceDiff <= 0.5) score += 1;

      // Same city/area in address (+2 points)
      const currentCity = currentProperty.address.split(",")[0].trim().toLowerCase();
      const propertyCity = property.address.split(",")[0].trim().toLowerCase();
      if (currentCity === propertyCity) score += 2;

      // Similar structure (+1 point)
      if (property.structure === currentProperty.structure) score += 1;

      properties.push({ ...property, similarityScore: score } as any);
    });

    // Sort by similarity score and return top matches
    return properties
      .sort((a: any, b: any) => (b.similarityScore || 0) - (a.similarityScore || 0))
      .slice(0, limitCount);
  } catch (error) {
    console.error("Error getting similar properties:", error);
    return [];
  }
}


